<template>
  <p v-if="isModal" :class="$style.backLink"><button @click.prevent="close">&lt; 戻る</button></p>
  <div :class="$style.content">
    <p :class="$style.signup"><a :href="`/signin?from=${from}`" @click.prevent="toggle">ログインはこちら</a></p>
    <h1 :class="$style.pageTitle">会員登録</h1>
    <div v-if="oauthProvider && socialProvider(oauthProvider)" :class="$style.socialConnected">
      <!-- eslint-disable vue/no-v-html -->
      <div :class="$style.socialConnectedSvg" v-html="socialProvider(oauthProvider)!.svg"></div>
      <!-- eslint-enable vue/no-v-html -->
      <div>{{ socialProvider(oauthProvider)!.label }}連携しています</div>
    </div>
    <Form :validation-schema="validationSchema" @invalid-submit="scrollToError" @submit="onSubmit">
      <ul :class="$style.formInner">
        <li>
          <ITextField v-model.trim="formData.email" name="email" title="メールアドレス" type="email" autocomplete="email" />
        </li>
        <li>
          <ITextField v-model.trim="formData.password" name="password" title="パスワード" type="password" autocomplete="new-password" />
        </li>
        <li>
          <ITextField v-model.trim="formData.memberName" name="memberName" title="ユーザー名" autocomplete="username" />
        </li>
      </ul>
      <div :class="$style.subscribeNewsletter">
        <label><input v-model="formData.subscribeNewsletter" type="checkbox" /> メールマガジンを購読する</label>
        <p :class="$style.note">
          会員登録には、<a href="/rule" target="_blank" class="text-decoration-underline">利用規約</a>と<a href="/privacy" target="_blank" class="text-decoration-underline">プライバシーポリシー</a>への同意が必要です。
        </p>
      </div>
      <div id="turnstile-container" :class="$style.turnstileWidget"></div>
      <IButton type="primary" native-type="submit" :class="$style.button">同意して会員登録する</IButton>
      <template v-if="!isWebView && oauthProvider === undefined">
        <p :class="$style.or"><span :class="$style.orInner">または</span></p>
        <ul :class="$style.socialBtns">
          <li v-for="item in connectArr" :key="item.id" :class="$style.socialBtn">
            <!-- eslint-disable vue/no-v-html -->
            <a :href="item.url" :class="$style.socialBtnLink" rel="nofollow"
              ><span :class="$style.socialBtnSvg"><span v-html="item.svg"></span></span>{{ item.label }}で登録</a
            >
            <!-- eslint-enable vue/no-v-html -->
          </li>
        </ul>
      </template>
      <p>
        <a href="https://help.iichi.com/hc/ja/articles/200196409" target="_blank">会員登録でお困りの方</a>
      </p>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { passwordSchema } from '@iichi/schema-api-v2/entities/password';
import { memberSchema } from '@iichi/schema-models';
import { toTypedSchema } from '@vee-validate/zod';
import { Form, type SubmissionHandler } from 'vee-validate';
import { reactive, computed, ref, onMounted } from 'vue';
import * as z from 'zod';
import { scrollToError } from '@/js/lib/helper/validate';
import { signup } from '@/js/lib/api/v1/user';
import { checkEmailExistence } from '@/js/lib/api/v1/validate';
import { useCommonStore } from '@/js/lib/store/common';
import { useUserStore } from '@/js/lib/store/user';
import { info, loading } from '@/js/lib/ui';
import { socialProviders } from '@/js/lib/ui/signin-signup';
import IButton from '@/js/components/molecules/IButton.vue';
import ITextField from '@/js/components/molecules/ITextField.vue';

interface FormData {
  email: string;
  password: string;
  memberName: string;
  subscribeNewsletter: boolean;
}

const props = withDefaults(
  defineProps<{
    email?: string;
    memberName?: string;
    transactionToken?: string;
    oauthProvider?: string;
    isModal?: boolean;
    from?: string;
    turnstileSiteKey: string;
  }>(),
  {
    email: '',
    memberName: '',
    transactionToken: '',
    oauthProvider: undefined,
    from: '',
  },
);
const emit = defineEmits<{
  (e: 'toggle'): void;
  (e: 'close'): void;
  (e: 'submit'): void;
}>();
const { isWebView } = useCommonStore();
const userStore = useUserStore();
const formData = reactive<FormData>({
  email: props.email,
  password: '',
  memberName: props.memberName,
  subscribeNewsletter: true,
});
const validationSchema = computed(() =>
  toTypedSchema(
    z.object({
      email: memberSchema.shape.email,
      password: passwordSchema,
      memberName: memberSchema.shape.memberName,
    }),
  ),
);
const connectArr = computed(() =>
  socialProviders.map((item) => {
    const from = props.from ? `?from=${props.from}` : '';
    const url = `/signup/with/${item.id}${from}`;
    return {
      id: item.id,
      label: item.label,
      svg: item.svg,
      url,
    };
  }),
);
const socialProvider = computed(() => (provider: string) => socialProviders.find((p) => p.id === provider));

const turnstileWidgetId = ref<string>('');
const isTurnstileInteractive = ref(false); // Turnstileウィジェットがインタラクティブになったかどうか

const onSubmit: SubmissionHandler = async (_values, { setFieldError }) => {
  const emailExistence = await checkEmailExistence({ email: formData.email });
  if (emailExistence !== true) {
    setFieldError('email', emailExistence);
    scrollToError();
    return;
  }

  if (!turnstileWidgetId.value) {
    info('会員登録フォームを正しく読み込めませんでした。時間をおいて再度お試しください。');
    return;
  }

  const turnstileToken = window.turnstile.getResponse(turnstileWidgetId.value);
  if (!turnstileToken) {
    if (isTurnstileInteractive.value) {
      info('「私はロボットではありません」をチェックして、セキュリティ認証を完了してください。');
    } else {
      info('正しく認証できませんでした。時間をおいて再度お試しください。');
    }
    return;
  }

  loading({
    asyncFunc: async () => {
      try {
        await signup({
          email: formData.email,
          password: formData.password,
          memberName: formData.memberName,
          subscribeNewsletter: formData.subscribeNewsletter,
          transactionToken: props.transactionToken,
          turnstileToken,
        });
        onSuccess();
      } catch (error) {
        window.Sentry?.captureException(error);
        window.turnstile.reset(turnstileWidgetId.value);
        info('会員登録できませんでした。時間をおいて再度お試しください。');
      }
    },
  });
};

const toggle = (event: Event) => {
  if (props.isModal) {
    emit('toggle');
  } else {
    window.location.href = (event.target as HTMLAnchorElement).href;
  }
};
const onSuccess = () => {
  userStore.setSignined();
  if (props.isModal) {
    emit('submit');
  } else {
    window.location.href = props.from || '/';
  }
};
const close = () => {
  emit('close');
};

onMounted(() => {
  if (!window.turnstile) {
    info('会員登録フォームを正しく読み込めませんでした。時間をおいて再度お試しください。');
    window.Sentry.captureMessage('Turnstile SDK is not loaded', 'error');
    return;
  }

  const turnstileContainerElement = document.querySelector<HTMLElement>('#turnstile-container');
  if (turnstileContainerElement) {
    turnstileWidgetId.value =
      window.turnstile.render(turnstileContainerElement, {
        appearance: 'interaction-only',
        sitekey: props.turnstileSiteKey,
        size: 'flexible',
        theme: 'light',
        'before-interactive-callback'() {
          isTurnstileInteractive.value = true;
        },
        async 'error-callback'() {
          // 認証に失敗した場合
          await info('正しく認証できませんでした。時間をおいて再度お試しください。');
          window.turnstile.reset(turnstileWidgetId.value);
        },
      }) || '';
  }
});
</script>

<style module lang="scss">
.backLink {
  left: 20px;
  position: absolute;
  top: 25px;

  > button {
    font-size: 16px;

    @media (--sp) {
      font-size: 14px;
    }
  }
}

.content {
  margin: 0 auto;
  max-width: 480px;
  text-align: center;
  width: 100%;

  @media (--pc) {
    min-height: 840px;
  }
}

.signup {
  font-weight: bold;
  margin-bottom: 16px;
  text-align: right;
}

.pageTitle {
  font-size: 24px;
  margin-bottom: 30px;
}

.socialConnected {
  align-items: center;
  display: flex;
  margin-bottom: 25px;

  .socialConnectedSvg {
    margin-right: 8px;

    > svg {
      vertical-align: bottom;
    }
  }
}

.formInner > li {
  margin-bottom: 20px;
}

.subscribeNewsletter {
  margin-bottom: 20px;
  text-align: left;

  label {
    cursor: pointer;
  }
}

.note {
  font-size: 14px;
  margin: 25px 0 0;
}

.button {
  margin-bottom: 20px;
}

.or {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
  padding: 10px 0;
  position: relative;

  &::before {
    border-top: 1px solid var(--color-black);
    content: '';
    position: absolute;
    top: 50%;
    width: 100%;
  }

  .orInner {
    background-color: var(--color-white);
    display: inline-block;
    padding: 0 10px;
    position: relative;
  }
}

.socialBtns {
  padding-bottom: 10px;
}

.socialBtn {
  margin-bottom: 14px;
  position: relative;

  .socialBtnLink {
    border: 1px solid var(--color-black);
    border-radius: 4px;
    display: block;
    padding: 16px;
    text-decoration: none;

    &:hover {
      opacity: 0.8;
    }

    .socialBtnSvg {
      left: 18px;
      position: absolute;
      width: 24px;
    }
  }
}

.turnstileWidget {
  margin-bottom: 16px;
}
</style>
